/**
 * Mana base analysis hook for deck building
 *
 * Computes land recommendations, color source analysis, and mana base health
 * using hypergeometric probability calculations for dynamic turn-based recommendations.
 */
import { useMemo } from 'react';

import { countManaPips, getColorName, getDisplayManaCost } from '@/lib/cardUtils';
import type { Color } from '@/types/card';
import type { DeckFormat } from '@/types/deck';

import { cardsByTurn, getDefaultTurn, minimumSourcesForTurn } from '../lib/manamath';
import type { EnrichedDeckCard } from '../types';

// ============================================================================
// Types
// ============================================================================

export interface LandRecommendation {
  /** Recommended land count based on format and curve */
  recommended: number;
  /** Current land count */
  current: number;
  /** Difference (positive = too many, negative = too few) */
  difference: number;
  /** Mana dork/rock count (each counts as 0.5 land) */
  manaSourceCount: number;
  /** Effective land count (lands + 0.5 × mana sources) */
  effectiveLandCount: number;
  /** Whether the recommendation is significantly off */
  isWarning: boolean;
  /** Human-readable suggestion */
  suggestion: string | null;
}

export interface ColorSourceCount {
  /** Color being analyzed */
  color: Color;
  /** Number of sources that produce this color */
  sources: number;
  /** Recommended sources based on Frank Karsten's table */
  recommended: number;
  /** Number of pips of this color in deck */
  pips: number;
  /** Status: 'good' | 'warning' | 'critical' */
  status: 'good' | 'warning' | 'critical';
  /** Human-readable suggestion */
  suggestion: string | null;
}

export interface ColorSourceAnalysis {
  /** Analysis per color */
  colors: ColorSourceCount[];
  /** Overall mana base health score (0-100) */
  healthScore: number;
}

export interface ManaBaseHealth {
  /** Basic land count */
  basicLands: number;
  /** Non-basic land count */
  nonBasicLands: number;
  /** Utility lands count (lands with non-mana abilities) */
  utilityLands: number;
  /** Basic/nonbasic ratio display string */
  basicRatio: string;
  /** Mana dorks count */
  manaDorks: number;
  /** Mana rocks count */
  manaRocks: number;
  /** Total mana sources (lands + dorks + rocks) */
  totalSources: number;
}

export interface ManaBaseAnalysis {
  landRecommendation: LandRecommendation;
  colorSourceAnalysis: ColorSourceAnalysis;
  manaBaseHealth: ManaBaseHealth;
}

// ============================================================================
// Constants
// ============================================================================

/** Format-based land recommendations */
const FORMAT_LAND_DEFAULTS: Record<string, { base: number; perCmc: number }> = {
  standard: { base: 21, perCmc: 1.0 }, // +1 per 0.3 avg CMC above 2.5
  pioneer: { base: 21, perCmc: 1.0 },
  modern: { base: 20, perCmc: 1.0 },
  legacy: { base: 18, perCmc: 0.8 }, // Lower due to free spells
  vintage: { base: 16, perCmc: 0.6 },
  pauper: { base: 20, perCmc: 1.0 },
  commander: { base: 33, perCmc: 1.5 }, // +1.5 per 0.2 avg CMC above 3.0
  brawl: { base: 23, perCmc: 1.2 },
  oathbreaker: { base: 23, perCmc: 1.2 },
  casual: { base: 22, perCmc: 1.0 },
  draft: { base: 17, perCmc: 0.5 }, // Limited: 17 lands base, slight adjustment for curve
  sealed: { base: 17, perCmc: 0.5 }, // Limited: same as draft
  jumpstart: { base: 17, perCmc: 0.5 }, // Limited: same as draft/sealed
};

/** Limited formats that use 40-card decks */
const LIMITED_FORMATS = ['draft', 'sealed', 'jumpstart'];

const DEFAULT_LAND_CONFIG = { base: 22, perCmc: 1.0 };

// ============================================================================
// Mana Base Calculation Helpers
// ============================================================================

/**
 * Calculate recommended lands based on format and average CMC
 */
function calculateRecommendedLands(format: DeckFormat | null, avgCmc: number): number {
  const config = format ? (FORMAT_LAND_DEFAULTS[format] ?? DEFAULT_LAND_CONFIG) : DEFAULT_LAND_CONFIG;
  const isLimited = format && LIMITED_FORMATS.includes(format);

  // Base lands
  let recommended = config.base;

  // Commander uses different baseline CMC, limited uses ~3.0 average
  const baselineCmc = format === 'commander' ? 3.0 : isLimited ? 3.0 : 2.5;

  // Adjust for curve
  if (avgCmc > baselineCmc) {
    const cmcDiff = avgCmc - baselineCmc;
    const adjustment = format === 'commander' ? cmcDiff / 0.2 : cmcDiff / 0.3;
    recommended += Math.round(adjustment * config.perCmc);
  } else if (avgCmc < baselineCmc - 0.5) {
    // Slightly reduce for very low curves
    recommended -= 1;
  }

  // Clamp to reasonable values by format type
  if (format === 'commander') {
    return Math.max(33, Math.min(42, recommended));
  }
  if (isLimited) {
    return Math.max(15, Math.min(18, recommended));
  }
  return Math.max(18, Math.min(28, recommended));
}

/**
 * Get recommended sources for a pip count using hypergeometric probability.
 * Calculates minimum sources needed to hit `pipCount` colored sources
 * by the target turn with 90% reliability.
 */
function getRecommendedSources(pipCount: number, deckSize: number, targetTurn: number): number {
  if (pipCount <= 0) return 0;
  const pips = Math.min(pipCount, 4); // Cap at 4 pips
  return minimumSourcesForTurn(deckSize, cardsByTurn(targetTurn), pips, 0.9);
}

// ============================================================================
// Main Hook
// ============================================================================

/**
 * Compute comprehensive mana base analysis for a deck.
 *
 * @param cards - Array of deck cards with card data
 * @param format - Deck format for format-specific recommendations
 * @param targetTurn - Target turn for color source recommendations (default: format-dependent)
 * @param zones - Optional zones to include (defaults to main deck only)
 */
export default function useManaBaseAnalysis(
  cards: EnrichedDeckCard[],
  format: DeckFormat | null,
  targetTurn?: number,
  zones: string[] = ['main', 'commander', 'companion'],
): ManaBaseAnalysis {
  const turn = targetTurn ?? getDefaultTurn(format);

  return useMemo(() => {
    // Filter to specified zones
    const relevantCards = cards.filter((c) => zones.includes(c.zone));

    // ========================================================================
    // Categorize cards
    // ========================================================================

    let totalCards = 0;
    let landCount = 0;
    let basicLandCount = 0;
    let nonBasicLandCount = 0;
    let utilityLandCount = 0;
    let manaDorkCount = 0;
    let manaRockCount = 0;

    // Color sources per color
    const colorSources: Record<Color, number> = { W: 0, U: 0, B: 0, R: 0, G: 0 };

    // Color pips in deck (for recommendation calculation)
    const colorPips: Record<Color, number> = { W: 0, U: 0, B: 0, R: 0, G: 0 };

    // CMC tracking for average calculation
    let cmcSum = 0;
    let nonLandCount = 0;

    for (const deckCard of relevantCards) {
      const card = deckCard.card;
      const qty = deckCard.quantity;

      totalCards += qty;

      const cardIsLand = deckCard.classification.is_land;

      if (cardIsLand) {
        landCount += qty;

        // Categorize land type
        if (deckCard.classification.is_basic_land) {
          basicLandCount += qty;
        } else {
          nonBasicLandCount += qty;
        }

        if (deckCard.classification.is_utility_land) {
          utilityLandCount += qty;
        }

        // Count color sources from land (pre-computed during enrichment)
        for (const color of deckCard.land_colors) {
          colorSources[color] += qty;
        }
      } else {
        // Non-land card
        nonLandCount += qty;
        cmcSum += (card.cmc ?? 0) * qty;

        // Check for mana dorks (pre-computed classification)
        if (deckCard.classification.is_mana_dork) {
          manaDorkCount += qty;
          for (const color of deckCard.mana_source_colors) {
            colorSources[color] += qty;
          }
        }

        // Check for mana rocks (pre-computed classification)
        if (deckCard.classification.is_mana_rock) {
          manaRockCount += qty;
          for (const color of deckCard.mana_source_colors) {
            colorSources[color] += qty;
          }
        }

        // Count mana pips
        const pips = countManaPips(getDisplayManaCost(card));
        for (const [color, count] of Object.entries(pips)) {
          if (color !== 'C' && color in colorPips) {
            colorPips[color as Color] += count * qty;
          }
        }
      }
    }

    const avgCmc = nonLandCount > 0 ? cmcSum / nonLandCount : 0;

    // ========================================================================
    // Land Recommendation
    // ========================================================================

    const manaSourceCount = manaDorkCount + manaRockCount;
    const effectiveLandCount = landCount + manaSourceCount * 0.5;
    const recommendedLands = calculateRecommendedLands(format, avgCmc);
    const difference = landCount - recommendedLands;
    const isLandWarning = Math.abs(difference) >= 3;

    let landSuggestion: string | null = null;
    if (isLandWarning) {
      if (difference > 0) {
        landSuggestion = `Consider removing ${difference} land${difference !== 1 ? 's' : ''}`;
      } else {
        landSuggestion = `Consider adding ${-difference} more land${difference !== -1 ? 's' : ''}`;
      }
    }

    const landRecommendation: LandRecommendation = {
      recommended: recommendedLands,
      current: landCount,
      difference,
      manaSourceCount,
      effectiveLandCount,
      isWarning: isLandWarning,
      suggestion: landSuggestion,
    };

    // ========================================================================
    // Color Source Analysis
    // ========================================================================

    // Find max pips for any single color (determines deck's color demands)
    const maxPipsPerColor: Record<Color, number> = { W: 0, U: 0, B: 0, R: 0, G: 0 };

    for (const deckCard of relevantCards) {
      const card = deckCard.card;
      if (!card || deckCard.classification.is_land) continue;

      const pips = countManaPips(getDisplayManaCost(card));
      for (const [color, count] of Object.entries(pips)) {
        if (color !== 'C' && color in maxPipsPerColor) {
          maxPipsPerColor[color as Color] = Math.max(maxPipsPerColor[color as Color], count);
        }
      }
    }

    const colorAnalysis: ColorSourceCount[] = [];

    for (const color of ['W', 'U', 'B', 'R', 'G'] as Color[]) {
      const pips = colorPips[color];
      const sources = colorSources[color];

      // Skip colors with no pips
      if (pips === 0) continue;

      // Calculate recommended sources based on max pips in any single card
      const maxPips = maxPipsPerColor[color];
      const recommended = getRecommendedSources(maxPips, totalCards, turn);

      // Determine status
      let status: 'good' | 'warning' | 'critical';
      let suggestion: string | null = null;

      const sourceDiff = sources - recommended;

      if (sources >= recommended) {
        status = 'good';
      } else if (sources >= recommended - 3) {
        status = 'warning';
        suggestion = `Add ${-sourceDiff} more ${getColorName(color)} source${sourceDiff !== -1 ? 's' : ''}`;
      } else {
        status = 'critical';
        suggestion = `Need ${-sourceDiff} more ${getColorName(color)} sources for reliable casting`;
      }

      colorAnalysis.push({
        color,
        sources,
        recommended,
        pips,
        status,
        suggestion,
      });
    }

    // Calculate health score
    let healthScore = 100;
    for (const analysis of colorAnalysis) {
      if (analysis.status === 'warning') {
        healthScore -= 15;
      } else if (analysis.status === 'critical') {
        healthScore -= 30;
      }
    }
    healthScore = Math.max(0, healthScore);

    const colorSourceAnalysis: ColorSourceAnalysis = {
      colors: colorAnalysis,
      healthScore,
    };

    // ========================================================================
    // Mana Base Health
    // ========================================================================

    const basicRatio =
      landCount > 0 ? `${basicLandCount}/${nonBasicLandCount} (${Math.round((basicLandCount / landCount) * 100)}% basic)` : '0/0';

    const manaBaseHealth: ManaBaseHealth = {
      basicLands: basicLandCount,
      nonBasicLands: nonBasicLandCount,
      utilityLands: utilityLandCount,
      basicRatio,
      manaDorks: manaDorkCount,
      manaRocks: manaRockCount,
      totalSources: landCount + manaDorkCount + manaRockCount,
    };

    return {
      landRecommendation,
      colorSourceAnalysis,
      manaBaseHealth,
    };
  }, [cards, format, turn, zones]);
}
